import React, { FC, useCallback, useMemo } from 'react';
import { Box } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { useIsDesktop } from '@/ui-kit/hooks/useIsDesktop';
import { useDoVerifyResetPassword } from './hooks/useDoVerifyResetPassword';
import { PageTitle } from '@/ui-kit/components';
import { EmailOTPCodeForm } from '@/bo-kit/components/EmailOTPCodeForm';
import { UserType } from '@/bo-kit/types';
import { useDoResetPasswordSendCode } from '../../../ResetPassword/ResetPasswordForm/hook/useDoResetPasswordSendCode';

export const VerifyCode: FC<{
    email: string;
    password: string;
    confirmPassword: string;
    userType?: UserType;
}> = ({ email, password, confirmPassword, userType }) => {
    const { t } = useTranslation('bo-kit__signup');
    const { t: validationT } = useTranslation('bo-kit__validation');
    const { doVerify, isLoading, error, errorMessage } = useDoVerifyResetPassword();
    const validationSchema = useMemo(
        () =>
            Yup.object().shape({
                confirmationCode: Yup.string()
                    .required(validationT('Field is required'))
                    .matches(/^[A-Z0-9]*/, validationT('verificationCodeInvalid')),
            }),
        [validationT]
    );

    const isDesktop = useIsDesktop();

    const {
        doSendCode,
        error: errorResendCode,
        errorMessage: errorMessageResendCode,
    } = useDoResetPasswordSendCode();

    const doResendCode = useCallback(() => {
        userType != undefined ? doSendCode({ userType, email }) : doSendCode({ email });
    }, [doSendCode, email, userType]);

    const doSubmit = useCallback(
        ({ confirmationCode }: { confirmationCode: string }) => {
            doVerify({
                confirm_password: confirmPassword,
                password,
                code: confirmationCode,
            });
        },
        [confirmPassword, doVerify, password]
    );
    return (
        <>
            <Formik<{ confirmationCode: string }>
                initialValues={{ confirmationCode: '' }}
                onSubmit={doSubmit}
                validationSchema={validationSchema}
            >
                {(formik) => (
                    <form onSubmit={formik.handleSubmit}>
                        <Box sx={{ maxWidth: isDesktop ? 300 : 'unset' }}>
                            <PageTitle>{t('Reset password')}</PageTitle>
                            <EmailOTPCodeForm
                                email={email}
                                isError={!!error || !!errorResendCode}
                                errorMessage={errorMessage ?? errorMessageResendCode}
                                doResendCode={doResendCode}
                                formik={formik}
                                isLoading={isLoading}
                                submitBtnCaption={t('Reset password')}
                            />
                        </Box>
                    </form>
                )}
            </Formik>
        </>
    );
};
